import { useRef } from 'react'
import { fbt } from 'fbtee'
import { ChevronRight, UserPlus } from 'lucide-react'
import { beaconUserFallback } from '../../lib/commonStrings'
import { favoriteRingClass } from '../../lib/favoriteRing'
import { placeholders } from '../../lib/skeletonUtils'
import { cn } from '../../lib/utils'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { edgeMask } from '../../lib/edgeMask'
import { useScrollEdges } from '../../hooks/useScrollEdges'
import type { FriendOverview } from '../../hooks/useFriendsOverview'
import { Section } from '../../components/Section'
import { UserAvatar } from '../../components/UserAvatar'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import type { UserProfile } from '../../types'

// String(): the "Faro user" fallback is an fbt result, not a primitive string.
const firstName = (name: string) => String(name).split(/\s+/)[0]

/** Crew's friends: a scrollable row of faces (favorites first), an Add button, and a way into the full list. */
export function FriendsStrip({
  friends,
  profiles,
  loading,
  onAdd,
}: {
  friends: FriendOverview[]
  profiles: Record<string, UserProfile>
  loading: boolean
  onAdd: () => void
}) {
  const navigate = useTransitionNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { overflowLeft, overflowRight } = useScrollEdges(scrollRef)
  const mask = edgeMask(overflowLeft, overflowRight, '48px')

  return (
    <Section
      title={<>{fbt('Friends', 'Section heading for your friends in the Crew tab')} · {friends.length}</>}
      action={
        <Button variant="ghost" size="sm" className="h-7 gap-0.5 px-2 text-xs" onClick={() => navigate('/friends')}>
          {fbt('See all', 'Button in the Crew tab friends section that opens the full friends list')}
          <ChevronRight className="size-3.5" />
        </Button>
      }
    >
      <div
        ref={scrollRef}
        className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
        data-testid="crew-friends-strip"
      >
        <button type="button" onClick={onAdd} className="flex w-14 shrink-0 cursor-pointer flex-col items-center gap-1">
          <span className="flex size-12 items-center justify-center rounded-full border border-dashed border-border text-primary">
            <UserPlus className="size-5" />
          </span>
          <span className="text-[11px] text-muted-foreground">
            {fbt('Add', 'Button at the start of the Crew friends row that opens the add-a-friend sheet')}
          </span>
        </button>
        {loading
          ? placeholders(4).map((_, i) => (
              <div key={i} className="flex w-14 shrink-0 flex-col items-center gap-1">
                <Skeleton className="size-12 rounded-full" />
                <Skeleton className="h-3 w-10" />
              </div>
            ))
          : friends.map(({ uid, favorite }) => {
              const name = profiles[uid]?.displayName?.trim() || beaconUserFallback()
              return (
                <button
                  key={uid}
                  type="button"
                  onClick={() => navigate(`/users/${uid}`)}
                  className="flex w-14 shrink-0 cursor-pointer flex-col items-center gap-1"
                >
                  <UserAvatar
                    profile={profiles[uid]}
                    fallbackName={name}
                    size="xl"
                    className={cn('size-12', favorite && favoriteRingClass)}
                  />
                  <span className="w-full truncate text-center text-[11px]">{firstName(name)}</span>
                </button>
              )
            })}
      </div>
    </Section>
  )
}
